export const fetchTrafficPatterns = async ({
  tourData,
  selectedTour,
  departureTimeOffset = 0,
  urlPOST,
  GETapiBase,
  apiKey,
  onSuccess,
  signal,
}) => {
  const currentTour = tourData?.tours?.[selectedTour];

  if (!currentTour || !currentTour.stops || currentTour.stops.length === 0) {
    return false;
  }

  const origins = currentTour.stops
    .filter((stop) => stop.location && stop.location.lat && stop.location.lng)
    .map((stop) => ({
      lat: stop.location.lat,
      lng: stop.location.lng,
    }));

  if (origins.length === 0) {
    return false;
  }

  const stopsWithTimes = currentTour.stops.filter((stop) => stop.time);
  if (stopsWithTimes.length === 0) {
    return false;
  }

  const firstStop = stopsWithTimes[0];
  const lastStop = stopsWithTimes[stopsWithTimes.length - 1];

  const originalFromTime = firstStop.time?.arrival;
  const originalToTime = lastStop.time?.departure;

  if (!originalFromTime || !originalToTime) {
    return false;
  }

  // Apply departure time offset and add 4-hour buffer
  const adjustedFromTime = new Date(
    new Date(originalFromTime).getTime() + departureTimeOffset - 4 * 60 * 60 * 1000,
  ).toISOString();
  const adjustedToTime = new Date(
    new Date(originalToTime).getTime() + departureTimeOffset + 4 * 60 * 60 * 1000,
  ).toISOString();

  const requestBody = {
    origins,
    fromTime: adjustedFromTime,
    toTime: adjustedToTime,
    timeInterval: 60,
  };

  try {
    const matrixResponse = await fetch(`${urlPOST}${apiKey}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
      signal,
    });

    if (!matrixResponse.ok) {
      throw new Error(`Traffic patterns API request failed: ${matrixResponse.status}`);
    }

    const matrixData = await matrixResponse.json();
    const { matrixId } = matrixData;

    if (!matrixId) {
      return false;
    }

    // Poll for factors data until matrix is available
    const pollForFactors = async () => {
      const maxAttempts = 30; // Maximum number of polling attempts
      const pollInterval = 2000; // 2 seconds between attempts

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        if (signal?.aborted) return false;
        try {
          // eslint-disable-next-line no-await-in-loop
          const factorsResponse = await fetch(`${GETapiBase}${matrixId}?apiKey=${apiKey}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
            signal,
          });

          if (factorsResponse.ok) {
            // eslint-disable-next-line no-await-in-loop
            const factorsData = await factorsResponse.json();

            // Check if matrix data is available
            if (factorsData.matrix && factorsData.matrix.timestamps) {
              onSuccess(factorsData, origins, departureTimeOffset);
              return true; // Success - stop polling
            }
          } else {
            console.warn(`Polling attempt ${attempt} failed: ${factorsResponse.status}`);
          }

          // Only wait if this isn't the last attempt
          if (attempt < maxAttempts) {
            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolve, reject) => {
              const timer = setTimeout(resolve, pollInterval);
              signal?.addEventListener('abort', () => {
                clearTimeout(timer);
                reject(new DOMException('Aborted', 'AbortError'));
              });
            });
          }
        } catch (pollError) {
          if (pollError.name === 'AbortError') throw pollError;
          // Only wait if this isn't the last attempt
          if (attempt < maxAttempts) {
            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolve) => setTimeout(resolve, pollInterval));
          }
        }
      }

      throw new Error('Traffic factors polling timed out - matrix data not available');
    };

    await pollForFactors();
    return true;
  } catch (err) {
    if (err.name === 'AbortError') return false;
    console.warn(`Traffic API error: ${err.message}`);
    return false;
  }
};
